import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import { ExportButtons } from "@/lib/exportData";
import { isLegacyStoreContent } from "@/lib/xplosivContent";

const empty = { title: "", slug: "", excerpt: "", content: "", cover_image: "", author: "X-Plosiv Team", category: "Event Planning", tags: [], is_published: true };
const CATS = ["Event Planning", "Corporate Events", "Weddings", "Experiences", "Behind the Scenes"];

export default function AdminBlog() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);

    const load = async () => setItems((await api.get("/admin/blog")).data.filter((item) => !isLegacyStoreContent(item)));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map(s => s.trim()).filter(Boolean) };
            if (editing) await api.put(`/admin/blog/${editing}`, payload);
            else await api.post("/admin/blog", payload);
            toast.success("Saved");
            setEditing(null); setForm(empty); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete post?")) return;
        await api.delete(`/admin/blog/${id}`); load();
    };
    const edit = (p) => {
        setEditing(p.id);
        setForm({ ...p, tags: (p.tags || []).join(", ") });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div data-testid="admin-blog-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="font-display text-4xl">Blog / Journal</h1>
                    <p className="text-muted-foreground mt-1">{items.length} posts · markdown supported (##, **bold**)</p>
                </div>
                <ExportButtons rows={items} filename="xplosiv_event_journal" columns={["title", "slug", "category", "author", "is_published", "created_at"]} />
            </div>
            <form onSubmit={save} className="mt-6">
                <Card className="rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6 grid sm:grid-cols-2 gap-3">
                        <Fld label="Title"><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></Fld>
                        <Fld label="Slug"><Input required value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} /></Fld>
                        <div className="sm:col-span-2">
                            <Label className="font-mono-ui mb-1.5 block">Cover image</Label>
                            <ImageUploader value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} />
                        </div>
                        <Fld label="Author"><Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} /></Fld>
                        <Fld label="Category">
                            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                                <SelectTrigger className="rounded-2xl"><SelectValue /></SelectTrigger>
                                <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                        </Fld>
                        <Fld label="Tags (comma)"><Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} /></Fld>
                        <div className="sm:col-span-2"><Fld label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} /></Fld></div>
                        <div className="sm:col-span-2"><Fld label="Content (markdown)"><Textarea rows={10} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="font-mono text-xs" /></Fld></div>
                        <div className="flex items-center justify-between sm:col-span-2"><Label>Published</Label><Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} /></div>
                        <div className="sm:col-span-2 flex gap-3">
                            <button type="submit" className="dw-btn-primary px-5 py-2 font-mono-ui" data-testid="admin-blog-save">{editing ? "Update post" : "Publish post"}</button>
                            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="dw-btn-secondary px-5 py-2 font-mono-ui">Cancel</button>}
                        </div>
                    </CardContent>
                </Card>
            </form>

            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(p => (
                    <Card key={p.id} className="rounded-[20px] border-[#E7DED2] bg-white/80 overflow-hidden">
                        {p.cover_image && <img src={p.cover_image} alt="" className="aspect-[16/9] w-full object-cover" />}
                        <CardContent className="p-4">
                            <p className="font-mono-ui text-xs text-muted-foreground">{p.category} · {p.is_published ? "Live" : "Draft"}</p>
                            <h3 className="font-display text-xl mt-1 line-clamp-2">{p.title}</h3>
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.excerpt}</p>
                            <div className="mt-3 flex gap-2">
                                <button onClick={() => edit(p)} className="text-xs font-mono-ui underline"><Pencil className="h-3 w-3 inline" /> Edit</button>
                                <button onClick={() => del(p.id)} className="text-xs font-mono-ui text-[#B42318]"><Trash2 className="h-3 w-3 inline" /> Delete</button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

function Fld({ label, children }) { return <div><Label className="font-mono-ui mb-1.5 block">{label}</Label>{children}</div>; }
